import moment from 'moment';
import { VAS } from '../segment/types';
import { PRODUCTION_ENVIRONMENT } from '../api/constants';
import { Category } from 'src/international-insurance/config';
import { MONTH_SHORT_FORMS } from 'src/common/components/date-range/common/constants';

export const getImageBasePath = (imagePath: string) => {
  const imageStaticPath = process.env.NEXT_ASSET_PREFIX
    ? process.env.NEXT_ASSET_PREFIX.split('/next_assets')[0]
    : '';
  const basePath = imageStaticPath
    ? `${imageStaticPath}/_next_static`
    : '/_next_static';
  if (basePath === '/_next_static') {
    return imagePath;
  }
  return `${basePath}${imagePath}`;
};

export const getLabel = (label = '', selectedCountries: any[] = []) => {
  const length = selectedCountries?.length;

  if (length > 0) {
    const filtered = selectedCountries?.filter((_, i) => i < 2);
    label = filtered?.map(({ name }) => name)?.join(', ');

    if (length > 2) return label.concat(` and ${length - 2} more`);
  }

  return label;
};

export const getLabelWithCount = (selectedCountries: any[] = [], count = 1) => {
  let label = '';
  const length = selectedCountries?.length;

  if (length > 0) {
    const filtered = selectedCountries?.filter((_, i) => i < count);
    label = filtered?.map(({ name }) => name)?.join(', ');

    if (length > count) return label.concat(` +${length - count}`);
  }

  return label;
};

export const getTravellersLabel = (
  adultCount = 0,
  seniorCitizenCount = 0,
  childCount = 0
) => {
  const stringList = <any>[];

  const adultString =
    adultCount > 0
      ? adultCount > 1
        ? `${adultCount} ${Category.ADULT}s`
        : `${adultCount} ${Category.ADULT}`
      : '';

  if (adultString) stringList?.push(adultString);
  const childString =
    childCount > 0
      ? childCount > 1
        ? `${childCount} ${Category.CHILDREN}`
        : `${childCount} ${Category.CHILD}`
      : '';

  if (childString) stringList?.push(childString);

  const seniorString =
    seniorCitizenCount > 0
      ? seniorCitizenCount > 1
        ? `${seniorCitizenCount} senior citizens`
        : `${seniorCitizenCount} senior citizen`
      : '';

  if (seniorString) stringList?.push(seniorString);

  return stringList?.join(', ');
};

export const convertDateFormat = (date: Date) => {
  const formattedDate = moment(date).format('DD-MM-YYYY');

  return formattedDate;
};

export const getCountriesString = (countries: any = []) => {
  const names = countries?.map((c) => c?.name)?.join(', ');

  if (names?.length > 30) return names?.substring(0, 30)?.concat('...');
  return names;
};

export const getDurationString = (start, end) => {
  let nStart = moment(start, 'DD-MM-YYYY').format('D MMM YYYY');
  const nEnd = moment(end, 'DD-MM-YYYY').format('D MMM YYYY');

  if (nStart?.slice(-4) === nEnd?.slice(-4)) {
    const length = nStart?.length;
    nStart = nStart?.slice(0, length - 5);
  }
  return `${nStart} - ${nEnd}`;
};

export const getSegmentCountries = (list) => {
  if (list?.length > 0) {
    if (list?.length > 1) {
      const c = list?.map((c) => c?.name)?.slice(0, 2);
      return c?.join(', ');
    }
    return list[0]?.name;
  }
  return '';
};

export const calculateAge = (dob: any) => {
  const ageDifMs = Date.now() - moment(dob, 'DD-MM-YYYY').toDate().getTime();
  const ageDate = new Date(ageDifMs);
  return Math.abs(ageDate.getUTCFullYear() - 1970);
};

export const getUserType = (t) => {
  if (t?.length > 0) {
    const ages = t?.map(({ dob }) => (dob ? calculateAge(dob) : null));
    const seniorCitizen = ages?.filter((a: any) => a >= 60);
    return seniorCitizen?.length > 0 ? 'senior_citizen' : '';
  }
  return '';
};

export const handleBrowserBack = (router, handleBack) => {
  if (!!router) {
    router?.beforePopState(({ as }) => {
      if (as !== router?.asPath) {
        handleBack();
        return false;
      }
      return true;
    });

    return () => {
      router?.beforePopState(() => true);
    };
  }
  return false;
};

export function inputRegexValidation(text: string) {
  try {
    if (text.length === 0) {
      return false;
    }
    const regx = new RegExp('<|//|>|<iframe|http:|https:|@|$|#');
    if (regx.test(text)) {
      return false;
    }
    return true;
  } catch {
    return false;
  }
}

export function getFormattedDates(duration) {
  const startDateRegex = /\d{1,2} \w{3}(, \d{4})?/; // Matches day, month, and optional year pattern
  const endDateRegex = /\d{1,2} \w{3}, \d{4}/; // Matches day, month, and year pattern
  const startDateMatch = duration?.match(startDateRegex)[0];
  const startIndex = duration?.indexOf('-') + 1;
  const endDateMatch = duration?.substr(startIndex).match(endDateRegex)[0];

  const hasYear = startDateMatch?.includes(',');
  const startYear = hasYear
    ? moment(startDateMatch, 'DD MMM, YYYY').format('YYYY')
    : moment(endDateMatch, 'DD MMM, YYYY').format('YYYY');
  const endYear = moment(endDateMatch, 'DD MMM, YYYY').format('YYYY');

  const startDate = moment(
    `${startDateMatch} ${startYear}`,
    'DD MMM YYYY'
  ).format('DD/MM/YYYY');
  const endDate = moment(`${endDateMatch} ${endYear}`, 'DD MMM YYYY').format(
    'DD/MM/YYYY'
  );

  return {
    startDate: startDate !== 'Invalid date' ? startDate : null,
    endDate: endDate !== 'Invalid date' ? endDate : null,
  };
}

export const getQueryParams = (query: any = {}) => {
  const utmSource = query?.utm_source;
  let singleUtmSource = '';
  const attrs = Object.keys(query)
    .map((q) =>
      q !== 'journeyId' &&
      q !== 'utm_source' &&
      q !== 'visaCountry' &&
      q !== 'urlRoot'
        ? `${q}=${query[q]}`
        : null
    )
    ?.filter((a) => !!a);

  if (utmSource) {
    if (Array.isArray(utmSource) && utmSource?.length > 0) {
      singleUtmSource = `utm_source=${utmSource[0]}`;
    } else singleUtmSource = `utm_source=${query.utm_source}`;

    attrs?.push(singleUtmSource);
  }

  const params = attrs?.join('&');

  return params ? `?${params}` : '';
};

export const getSessionQuery = () => {
  if (typeof window !== 'undefined') {
    // Getting previous params from session storage
    const queryString = sessionStorage?.getItem('routerQuery');
    const sessionQuery = !!queryString ? JSON?.parse(queryString as any) : {};

    const vasQueryString = sessionStorage?.getItem(VAS.VAS_ROUTER_QUERY);
    const vasSessionQuery = !!vasQueryString
      ? JSON?.parse(vasQueryString as any)
      : {};

    return { ...vasSessionQuery, ...sessionQuery };
  }
  return {};
};

// Login redirection

export const redirectToLogin = () => {
  if (typeof window !== 'undefined') {
    const pathname = window?.location?.pathname || '';
    const search = window?.location?.search || '';
    const nextRoute = pathname + search;

    window.location.href = `/login?next=${encodeURIComponent(nextRoute)}`;
  }
};

export const convertDateToString = (inputFormat) => {
  const pad = (s) => {
    return s < 10 ? `0${s}` : s;
  };
  const d = new Date(inputFormat);
  return [pad(d.getDate()), pad(d.getMonth() + 1), d.getFullYear()].join('-');
};

export const getMonth = (input: any) => {
  const d = new Date(input);
  if (!!d) {
    const month = d?.getMonth() + 1;
    return month < 10 ? `0${month}` : month;
  }
  return '';
};

export const getYear = (input: any, century = 20) => {
  const y = new Date(input);
  if (!!y) {
    const year = y?.getFullYear();
    return year - century * 100;
  }
  return '';
};

export const getDomainHostname = () => {
  const prodUrl = 'https://www.acko.com';
  const uatUrl = 'https://www.ackodev.com';

  if (PRODUCTION_ENVIRONMENT) return prodUrl;
  return uatUrl;
};

export const getAtlPrice = (original_price = '', multiplier) => {
  const currencyFormatter = new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  });

  const priceString = !!original_price ? original_price?.slice(1) : '';
  const filteredPrice = priceString?.replace(',', '');
  const price = !!filteredPrice ? Number(filteredPrice) : 0;

  return currencyFormatter?.format(Math.ceil(price * multiplier));
};

export const hasDiscountedPrice = (priceBreakup: any) =>
  !!priceBreakup?.discount &&
  !!priceBreakup?.net_amount &&
  !!priceBreakup?.total_amount &&
  priceBreakup.net_amount !== priceBreakup.total_amount;

export const getTripDurationLabel = (selectedDateRange) => {
  if (selectedDateRange?.start && selectedDateRange?.end) {
    const startDate = {
      y: selectedDateRange?.start?.getFullYear(),
      m: selectedDateRange?.start?.getMonth(),
      d: selectedDateRange?.start?.getDate(),
    };
    const endDate = {
      y: selectedDateRange?.end?.getFullYear(),
      m: selectedDateRange?.end?.getMonth(),
      d: selectedDateRange?.end?.getDate(),
    };

    let label = `${startDate?.d} ${MONTH_SHORT_FORMS[startDate?.m]}, ${
      startDate?.y
    } - ${endDate?.d} ${MONTH_SHORT_FORMS[endDate?.m]}, ${endDate?.y}`;

    if (startDate?.y === endDate?.y) {
      label = `${startDate?.d} ${MONTH_SHORT_FORMS[startDate?.m]} - ${
        endDate?.d
      } ${MONTH_SHORT_FORMS[endDate?.m]}, ${endDate?.y}`;
    }

    return label;
  }
  return '';
};

export const convertDateRange = (dateRangeStr: string) => {
  const [startStr, endStr] = dateRangeStr?.split(' - ');
  const startDateYear = startStr?.split(',')[1]?.trim();
  const endDateYear = endStr?.split(',')[1]?.trim();

  const startDate = startDateYear
    ? new Date(startStr)
    : new Date(`${startStr}, ${endDateYear}`);

  const endDate = new Date(endStr);

  return [startDate, endDate];
};

export const closeWebView = () => {
  const webViewData = { action: 'close_webview' };
  const stringWebViewData = JSON.stringify(webViewData);
  const flutterWebView = (window as any).flutter_inappwebview;

  if (flutterWebView?.callHandler) {
    flutterWebView.callHandler('WebViewConnect', stringWebViewData);
  }
};
